import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMyContext } from "../../context/MyContext";
import { api } from "../../config/api";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import AddPersonalTodoModal from "./AddPersonalTodoModal";
import { useState } from "react";
import { Dropdown, Menu, Modal, Space, Table, Tag } from "antd";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { CheckCircle, ChevronDown, Clock, Info, Loader2, PlayCircle } from "lucide-react";
import Loader from "../shared/loader/loader";

const statusOptions = [
  { value: "pending", label: "Pending", icon: Clock },
  { value: "In Progress", label: "In Progress", icon: PlayCircle },
  { value: "completed", label: "Completed", icon: CheckCircle },
];

const StatusDropdown = ({ status, onChange, id, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const getStatusColor = (statusValue) => {
    const lower = statusValue?.toLowerCase();
    switch (lower) {
      case "in progress":
        return {
          backgroundColor: "#fff9e0",
          color: "#FFC93F",
          borderColor: "#FFC93F",
        };
      case "completed":
        return {
          backgroundColor: "#dcfce7",
          color: "#166534",
          borderColor: "#86efac",
        };
      case "pending":
        return {
          backgroundColor: "#dbeafe",
          color: "#1e40af",
          borderColor: "#93c5fd",
        };
      default:
        return {
          backgroundColor: "#f3f4f6",
          color: "#374151",
          borderColor: "#d1d5db",
        };
    }
  };

  const isLoading = loading === id;

  const currentStatus = statusOptions.find(
    (opt) => opt.value.toLowerCase() === status?.toLowerCase()
  );
  const CurrentIcon = isLoading ? Loader2 : currentStatus?.icon || Info;

  const statusColors = getStatusColor(status);

  return (
    <div className="relative w-full max-w-xs">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-2 hover:shadow-md"
        style={{
          backgroundColor: isLoading ? "#fef3c7" : statusColors.backgroundColor,
          color: isLoading ? "#d97706" : statusColors.color,
          borderColor: isLoading ? "#d97706" : statusColors.borderColor,
        }}
      >
        <div className="flex items-center gap-2">
          <CurrentIcon
            className={`${isLoading && "animate-spin"}`}
            style={{ width: "16px", height: "16px" }}
          />
          <span className="text-sm font-medium capitalize truncate">
            {isLoading ? "Please wait..." : status || "Select Status"}
          </span>
        </div>
        <ChevronDown
          style={{
            width: "16px",
            height: "16px",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms",
          }}
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 min-w-[250px] mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-80 overflow-y-auto">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              const isSelected =
                option.value.toLowerCase() === status?.toLowerCase();

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(id, option.value);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                  style={{
                    backgroundColor: isSelected ? "#dbeafe" : undefined,
                    color: isSelected ? "#2563eb" : undefined,
                  }}
                >
                  <Icon size={20} />
                  <span
                    className={`text-sm font-medium whitespace-nowrap ${
                      isSelected ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    {option.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="w-4 h-4 ml-auto text-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
const PersonalTodos = () => {
  const { user, token, isAuthenticated } = useMyContext();
  const [openModal, setOpenModal] = useState(false);
  const [localTodos, setLocalTodos] = useState([]);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ["personal-todos", user?.uid],
    async () => {
      if (!token) throw new Error("No token available");
      const res = await api.get(`/todos/user/personal/${user?.uid}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: true,
      refetchOnMount: true,
      enabled: !!user && !!token && isAuthenticated,
      retry: 3,
      retryDelay: 1000,
      onSuccess: (fetchedData) => {
        const sortedData = sortTodosByDate(fetchedData);
        setLocalTodos(sortedData);
      },
      onError: (error) => {
        console.error("Error fetching personal todos:", error);
        toast.error("Failed to load personal todos. Please try again.");
      },
    }
  );

  const sortTodosByDate = (todos) => {
    return [...todos].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // newest first
    });
  };

  const updateStatusMutation = useMutation(
    ["task"],
    async (data) => {
      await api.put(`/todos/status/${data?.id}/${data?.status}`, {}, {
        headers: { authorization: `Bearer ${token}` },
      });
    },
    {
      onMutate: (variables) => {
        setLocalTodos((prevTodos) =>
          sortTodosByDate(
            prevTodos.map((todo) =>
              todo._id === variables.id ? { ...todo, status: variables.status } : todo
            )
          )
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["personal-todos", user?.uid]);
        queryClient.invalidateQueries(["todos"]);
        toast.success("Status updated!");
      },
      onError: () => {
        queryClient.invalidateQueries(["personal-todos", user?.uid]);
        toast.error("Failed to update status. Please try again.");
      },
    }
  );

  const deleteTodoMutation = useMutation(
    ["task-delete"],
    async (id) => {
      await api.delete(`/todos/${id}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      return id;
    },
    {
      onSuccess: (deletedId) => {
        queryClient.invalidateQueries(["personal-todos", user?.uid]);
        setLocalTodos((prev) =>
          sortTodosByDate(prev.filter((todo) => todo._id !== deletedId))
        );
        toast.success("Todo deleted!");
      },
      onError: (error) => {
        const msg =
          error.response?.data?.error || error.message || "Unknown error";
        toast.error(`Failed to delete todo: ${msg}`);
      },
    }
  );

  const handleStatusChange = async (id, status) => {
    updateStatusMutation.mutate({ id, status });

    if (status.toLowerCase() === "completed") {
      try {
        await api.post("/notifications", {
          userId: user?.uid,
          title: "Task Completed",
          message: `The task with ${user.name} has been marked as completed.`,
          linkText: "View Todo",
          link: `/todo`,
          role: "admin",
          type: "task-completed",
        });
        toast.success("Notification sent for task completion!");
      } catch (error) {
        console.error("Error sending notification:", error);
        toast.error("Failed to send notification.");
      }
    }
  };

  const handleDeleteClick = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this todo?",
      onOk: async () => {
        await deleteTodoMutation.mutateAsync(id);
      },
    });
  };

  if (!isAuthenticated) return <div>Loading...</div>;

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      render: (_, record) => dayjs(record?.date).format("DD MMM YYYY"),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      render: (_, record) => dayjs(record?.dueDate).format("DD MMM YYYY"),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (_, record) => record?.title || "-",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => (
        <StatusDropdown
          status={record?.status}
          onChange={handleStatusChange}
          id={record?._id}
          loading={updateStatusMutation.isLoading ? record?._id : null}
        />
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      render: (_, record) => (
        <Tag
          className={`capitalize text-white border-none px-2 w-fit flex items-center justify-center rounded-full ${
            record?.priority === "Normal"
              ? "bg-green-500"
              : record?.priority === "Medium"
              ? "bg-yellow-500"
              : record?.priority === "High"
              ? "bg-red-500"
              : "bg-red-900"
          }`}
        >
          {record?.priority}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Dropdown
          placement="bottomRight"
          overlay={
            <Menu>
              <Menu.Item onClick={() => handleDeleteClick(record?._id)} key="delete">
                <Space>Delete</Space>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Space>
            <button className="border relative p-1 rounded-md border-[#053F92]">
              <PiDotsThreeVerticalBold size={20} />
            </button>
          </Space>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="flex flex-col space-y-2">
      <button
        className="btnBg text-white text-sm rounded-md text-center py-3 block btnHover hover:text-white transition-colors duration-300 px-4 w-fit"
        onClick={() => setOpenModal(true)}
      >
        Add Todo
      </button>

      {isLoading ? (
        <Loader />
      ) : localTodos.length > 0 ? (
        <Table columns={columns} dataSource={localTodos} loading={isLoading} />
      ) : (
        <div className="text-center py-8 text-gray-500">
          No todos found. Click "Add Todo" to create one.
        </div>
      )}

      {openModal && (
        <AddPersonalTodoModal setOpenModal={setOpenModal} openModal={openModal} />
      )}
    </div>
  );
};

export default PersonalTodos;